import { useCallback, useEffect, useMemo, useState } from "react";

import tagService from "src/services/tagService";

function readWorkspaceId() {
  try {
    const parsed = JSON.parse(localStorage.getItem("whatsapp_setup") || "{}");
    return parsed.workspaceId || parsed.workspace_id || null;
  } catch {
    return null;
  }
}

/**
 * The workspace tag catalog + CRUD. Used by the /tags page and by any tag
 * picker.
 */
export default function useTags() {
  const workspaceId = useMemo(readWorkspaceId, []);
  const ready = Boolean(workspaceId);

  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!ready) {
      setLoading(false);
      setError("Connect a WhatsApp account to manage tags.");
      return;
    }
    try {
      setError("");
      const res = await tagService.list(workspaceId);
      setTags(res?.data?.data ?? []);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not load tags.");
    } finally {
      setLoading(false);
    }
  }, [ready, workspaceId]);

  useEffect(() => {
    load();
  }, [load]);

  const createTag = useCallback(
    async (payload) => {
      const res = await tagService.create(workspaceId, payload);
      const tag = res?.data?.data;
      if (tag) setTags((prev) => [...prev, tag].sort((a, b) => a.name.localeCompare(b.name)));
      return tag;
    },
    [workspaceId],
  );

  const updateTag = useCallback(
    async (tagId, payload) => {
      const res = await tagService.update(workspaceId, tagId, payload);
      const tag = res?.data?.data;
      if (tag) setTags((prev) => prev.map((t) => (t.id === tagId ? tag : t)));
      return tag;
    },
    [workspaceId],
  );

  const deleteTag = useCallback(
    async (tagId) => {
      await tagService.remove(workspaceId, tagId);
      setTags((prev) => prev.filter((t) => t.id !== tagId));
    },
    [workspaceId],
  );

  return { workspaceId, ready, tags, loading, error, reload: load, createTag, updateTag, deleteTag };
}
